import { useState, useRef, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import { AddSourceDialog } from "../components/AddSourceDialog";

interface NetworkInterface {
  name: string;
  up: boolean;
  loopback: boolean;
  addresses?: string[] | null;
}

interface NetworkSnapshot {
  interfaces: NetworkInterface[];
}

interface ConnectorHint {
  connectorId: string;
  confidence: "high" | "medium";
  preFill: Record<string, string>;
}

interface ScanResult {
  ip: string;
  hostname: string;
  openPorts: number[];
  hints: ConnectorHint[];
  deviceProps?: Record<string, string>;
}

interface ScanSession {
  id: string;
  subnet: string;
  interface: string;
  state: "running" | "done" | "cancelled";
  progress: number;
  results: ScanResult[];
  startedAt: string;
}

// Format an ISO8601 date as a human-readable relative string.
function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins !== 1 ? "s" : ""} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? "s" : ""} ago`;
  return new Date(iso).toLocaleDateString();
}

// Derive a /24 subnet string from an IPv4 address string.
function toSlash24(addr: string): string {
  const parts = addr.split(".");
  if (parts.length === 4) {
    return `${parts[0]}.${parts[1]}.${parts[2]}.0/24`;
  }
  return addr;
}

// Return the first non-loopback IPv4 address from an interface.
function firstIPv4(iface: NetworkInterface): string | null {
  for (const addr of iface.addresses ?? []) {
    if (addr.includes(".") && !addr.startsWith("169.254")) {
      return addr.split("/")[0];
    }
  }
  return null;
}

export function ScannerPage() {
  const [selectedIface, setSelectedIface] = useState<string>("");
  const [subnet, setSubnet] = useState<string>("");
  const [scanId, setScanId] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<{
    connectorId: string;
    fields: Record<string, string>;
    suggestedName?: string;
  } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Selected connector for shared-port rows (port 502, 44818, etc.)
  const rowSelection = useRef<Record<string, string>>({});
  // Prevent the restore effect from re-running after the initial mount restore.
  const hasRestoredRef = useRef(false);

  const queryClient = useQueryClient();

  const network = useQuery({
    queryKey: ["network"],
    queryFn: () => api.get<NetworkSnapshot>("/api/v1/network"),
    staleTime: 60_000,
  });

  // Bootstrap: fetch the most recent scan from the backend on mount.
  // 204 → undefined (no prior scan); otherwise restores the previous session.
  const latestScan = useQuery({
    queryKey: ["scan", "latest"],
    queryFn: () => api.get<ScanSession | undefined>("/api/v1/scan"),
    staleTime: Infinity,
  });

  // Restore scan state from the latest session on first load.
  useEffect(() => {
    if (hasRestoredRef.current) return;
    if (!latestScan.data) return;
    hasRestoredRef.current = true;
    setScanId(latestScan.data.id);
    setSubnet(latestScan.data.subnet);
    if (latestScan.data.interface) {
      setSelectedIface(latestScan.data.interface);
    }
  }, [latestScan.data]);

  const scan = useQuery({
    queryKey: ["scan", scanId],
    queryFn: () => api.get<ScanSession>(`/api/v1/scan/${scanId}`),
    refetchInterval: (query) =>
      query.state.data?.state === "running" ? 2000 : false,
    enabled: !!scanId,
  });

  const startMutation = useMutation({
    mutationFn: (body: { subnet: string; interface: string }) =>
      api.post<{ scanId: string }>("/api/v1/scan", body),
    onSuccess: (data) => {
      setScanId(data.scanId);
      // Refresh the "latest" cache so the next navigation restores this scan.
      queryClient.invalidateQueries({ queryKey: ["scan", "latest"] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.delete<void>(`/api/v1/scan/${scanId}`),
  });

  // When a network interface is selected, auto-populate the subnet.
  function handleIfaceChange(name: string) {
    setSelectedIface(name);
    const iface = (network.data?.interfaces ?? []).find((i) => i.name === name);
    if (iface) {
      const ip = firstIPv4(iface);
      if (ip) setSubnet(toSlash24(ip));
    }
  }

  function handleStart() {
    if (!subnet.trim()) return;
    setScanId(null);
    startMutation.mutate({ subnet: subnet.trim(), interface: selectedIface });
  }

  function handleCancel() {
    cancelMutation.mutate();
  }

  function openAddSource(hint: ConnectorHint, row: ScanResult) {
    const label = row.deviceProps?.PRODUCT_NAME ?? row.hostname;
    const suggestedName = label
      ? `${label} (${row.ip})`
      : `${hint.connectorId}-${row.ip}`;
    setPrefill({ connectorId: hint.connectorId, fields: hint.preFill, suggestedName });
    setDialogOpen(true);
  }

  const usableInterfaces = (network.data?.interfaces ?? []).filter(
    (i) => i.up && !i.loopback,
  );

  const session = scan.data;
  const isRunning = session?.state === "running";
  const results = session?.results ?? [];

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <div className="mb-6">
        <h1 className="font-display text-[28px] font-semibold tracking-tightest text-ink">
          Network Scanner
        </h1>
        <p className="mt-1 text-[13.5px] text-ash">
          Probe every host in a subnet and identify industrial protocol services.
        </p>
        <p className="mt-2 text-[12.5px] text-ash/80 leading-relaxed">
          <span className="font-medium text-ash">Unicast scan</span> — TCP-knocks every host in the subnet on common industrial ports (Modbus, EtherNet/IP, OPC UA, and more).{" "}
          <span className="font-medium text-ash">CIP discovery</span> — when EtherNet/IP is detected, queries the device directly to retrieve its vendor, model, and firmware.
        </p>
      </div>

      {/* Controls */}
      <div className="card mb-6 flex flex-wrap items-end gap-4 px-6 py-5">
        <div className="flex-1 min-w-[160px]">
          <label className="mb-1 block text-[12px] font-medium text-ash">
            Interface
          </label>
          <select
            className="input w-full"
            value={selectedIface}
            onChange={(e) => handleIfaceChange(e.target.value)}
            disabled={isRunning}
          >
            <option value="">— any —</option>
            {usableInterfaces.map((i) => (
              <option key={i.name} value={i.name}>
                {i.name}
                {firstIPv4(i) ? ` (${firstIPv4(i)})` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1 min-w-[200px]">
          <label className="mb-1 block text-[12px] font-medium text-ash">
            Subnet
          </label>
          <input
            className="input w-full font-mono text-[13px]"
            placeholder="192.168.1.0/24"
            value={subnet}
            onChange={(e) => setSubnet(e.target.value)}
            disabled={isRunning}
          />
        </div>

        <div className="flex gap-2">
          {!isRunning ? (
            <button
              className="btn-primary"
              onClick={handleStart}
              disabled={!subnet.trim() || startMutation.isPending}
            >
              {startMutation.isPending ? "Starting…" : "Scan Network"}
            </button>
          ) : (
            <button
              className="btn-danger"
              onClick={handleCancel}
              disabled={cancelMutation.isPending}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {startMutation.error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">
          {(startMutation.error as Error).message}
        </div>
      )}

      {/* Progress */}
      {session && (
        <div className="mb-4">
          <div className="mb-1 flex items-center justify-between text-[12px] text-ash">
            <span>
              {isRunning
                ? `Scanning… ${results.length} host${results.length !== 1 ? "s" : ""} found`
                : session.state === "cancelled"
                  ? "Scan cancelled"
                  : `Scan complete — ${results.length} host${results.length !== 1 ? "s" : ""} found`}
              {!isRunning && session.startedAt && (
                <span className="ml-2 text-ash/60">· {formatRelative(session.startedAt)}</span>
              )}
            </span>
            <span>{session.progress}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-rule">
            <div
              className="h-full rounded-full bg-accent transition-all"
              style={{ width: `${session.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Results table */}
      {results.length > 0 && (
        <div className="card overflow-hidden">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-rule bg-paper-subtle text-[11.5px] font-medium uppercase tracking-wide text-ash">
                <th className="px-4 py-3 text-left">IP Address</th>
                <th className="px-4 py-3 text-left">Device</th>
                <th className="px-4 py-3 text-left">Open Ports</th>
                <th className="px-4 py-3 text-left">Connectors</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule">
              {results.map((row) => (
                <ScanRow
                  key={row.ip}
                  row={row}
                  rowSelection={rowSelection}
                  onAddSource={openAddSource}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {session && results.length === 0 && session.state !== "running" && (
        <div className="rounded-md border border-rule px-6 py-10 text-center text-[13.5px] text-ash">
          No hosts found on {session.subnet}.
        </div>
      )}

      <AddSourceDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        prefill={prefill ?? undefined}
      />
    </div>
  );
}

interface ScanRowProps {
  row: ScanResult;
  rowSelection: React.MutableRefObject<Record<string, string>>;
  onAddSource: (hint: ConnectorHint, row: ScanResult) => void;
}

function ScanRow({ row, rowSelection, onAddSource }: ScanRowProps) {
  const [selectedConnectorId, setSelectedConnectorId] = useState<string>(
    () => row.hints[0]?.connectorId ?? "",
  );

  const hasMultipleHints = row.hints.length > 1;
  const selectedHint =
    row.hints.find((h) => h.connectorId === selectedConnectorId) ??
    row.hints[0];

  function handleSelect(id: string) {
    setSelectedConnectorId(id);
    rowSelection.current[row.ip] = id;
  }

  return (
    <tr className="hover:bg-paper-subtle">
      <td className="px-4 py-3 font-mono text-[12.5px] text-ink">
        {row.ip}
      </td>
      <td className="px-4 py-3">
        <div className="text-[13px] text-ink">
          {row.deviceProps?.PRODUCT_NAME || row.hostname || <span className="text-rule">—</span>}
        </div>
        {row.deviceProps?.VENDOR_NAME && (
          <div className="text-[11px] text-ash">{row.deviceProps.VENDOR_NAME}</div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {row.openPorts.map((p) => (
            <span
              key={p}
              className="rounded bg-rule/40 px-1.5 py-0.5 font-mono text-[11px] text-ash"
            >
              {p}
            </span>
          ))}
        </div>
      </td>
      <td className="px-4 py-3">
        {row.hints.length === 0 ? (
          <span className="text-ash">—</span>
        ) : hasMultipleHints ? (
          <div className="flex flex-wrap gap-1">
            {row.hints.map((h) => (
              <button
                key={h.connectorId}
                onClick={() => handleSelect(h.connectorId)}
                className={`rounded border px-2 py-0.5 text-[11.5px] transition-colors ${
                  selectedConnectorId === h.connectorId
                    ? "border-accent bg-accent/10 text-accent font-medium"
                    : "border-rule bg-paper text-ash hover:border-ash"
                }`}
              >
                {h.connectorId}
                {h.confidence === "medium" && (
                  <span className="ml-1 text-[10px] opacity-60">~</span>
                )}
              </button>
            ))}
          </div>
        ) : (
          <span
            className={`inline-flex items-center gap-1 text-[12.5px] ${
              row.hints[0].confidence === "medium" ? "text-amber-600" : "text-ink"
            }`}
          >
            {row.hints[0].connectorId}
            {row.hints[0].confidence === "medium" && (
              <span className="text-[10px] text-amber-500">possible</span>
            )}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {row.hints.length > 0 && selectedHint && (
          <button
            className="btn-sm btn-primary"
            onClick={() => onAddSource(selectedHint, row)}
          >
            Add Source
          </button>
        )}
      </td>
    </tr>
  );
}
